import { supabase } from './supabase';
import { logger } from './logger';
import { env } from './env';
import { modelConfig } from './model-config';

interface JournalPrompt {
  id: string;
  text: string;
  category: 'reflection' | 'gratitude' | 'action' | 'growth' | 'challenge';
  source?: string;
}

interface JournalContext {
  timeOfDay: 'morning' | 'afternoon' | 'evening';
  recentMoods: string[];
  activeGoals: string[];
  recentChallenges: string[];
  lastEntryDate: string | null;
  streakDays: number;
}

const TIME_BASED_PROMPTS: Record<string, JournalPrompt[]> = {
  morning: [
    { id: 'am-1', text: "What's your main focus for today?", category: 'action' },
    { id: 'am-2', text: "How are you feeling as you start the day?", category: 'reflection' },
    { id: 'am-3', text: "What's one thing you're grateful for this morning?", category: 'gratitude' }
  ],
  afternoon: [
    { id: 'pm-1', text: "How's your day going so far?", category: 'reflection' },
    { id: 'pm-2', text: "What progress have you made today?", category: 'action' },
    { id: 'pm-3', text: "What's been challenging today?", category: 'challenge' }
  ],
  evening: [
    { id: 'eve-1', text: "What was your biggest win today?", category: 'reflection' },
    { id: 'eve-2', text: "What did you learn today?", category: 'growth' },
    { id: 'eve-3', text: "What are you grateful for from today?", category: 'gratitude' }
  ]
};

function getTimeOfDay(): 'morning' | 'afternoon' | 'evening' {
  const hour = new Date().getHours();
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
}

export async function getJournalContext(userId: string): Promise<JournalContext> {
  const timeOfDay = getTimeOfDay();

  try {
    const [entriesResult, contextResult, profileResult] = await Promise.all([
      supabase
        .from('journal_entries')
        .select('mood, tags, created_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(5),
      supabase
        .from('user_coaching_context')
        .select('primary_goals, key_challenges')
        .eq('user_id', userId)
        .maybeSingle(),
      supabase
        .from('profiles')
        .select('streak_days')
        .eq('id', userId)
        .maybeSingle()
    ]);

    const recentMoods = entriesResult.data?.map((e: { mood: string }) => e.mood).filter(Boolean) || [];
    const lastEntry = entriesResult.data?.[0];

    return {
      timeOfDay,
      recentMoods,
      activeGoals: contextResult.data?.primary_goals || [],
      recentChallenges: contextResult.data?.key_challenges || [],
      lastEntryDate: lastEntry?.created_at || null,
      streakDays: profileResult.data?.streak_days || 0
    };
  } catch (error) {
    logger.error('Error getting journal context:', error);
    return {
      timeOfDay,
      recentMoods: [],
      activeGoals: [],
      recentChallenges: [],
      lastEntryDate: null,
      streakDays: 0
    };
  }
}

export async function generateSmartPrompts(userId: string): Promise<JournalPrompt[]> {
  const context = await getJournalContext(userId);
  const prompts: JournalPrompt[] = [];

  const timePrompts = TIME_BASED_PROMPTS[context.timeOfDay];
  if (timePrompts.length > 0) {
    prompts.push(timePrompts[Math.floor(Math.random() * timePrompts.length)]);
  }

  if (context.activeGoals.length > 0) {
    const goal = context.activeGoals[0];
    const goalText = typeof goal === 'string' ? goal : (goal as any).text || '';
    if (goalText) {
      prompts.push({
        id: 'goal-1',
        text: `How's your progress on "${goalText.substring(0, 50)}${goalText.length > 50 ? '...' : ''}"?`,
        category: 'action',
        source: 'goal'
      });
    }
  }

  if (context.recentMoods.includes('anxious') || context.recentMoods.includes('stressed')) {
    prompts.push({
      id: 'mood-1',
      text: "What's one thing you can do right now to feel calmer?",
      category: 'challenge',
      source: 'mood-pattern'
    });
  }

  if (context.recentChallenges.length > 0) {
    prompts.push({
      id: 'challenge-1',
      text: "What's one small step you can take on a current challenge?",
      category: 'challenge',
      source: 'challenge'
    });
  }

  const daysSinceEntry = context.lastEntryDate
    ? Math.floor((Date.now() - new Date(context.lastEntryDate).getTime()) / (1000 * 60 * 60 * 24))
    : 999;

  if (daysSinceEntry > 2) {
    prompts.unshift({
      id: 'return-1',
      text: "Welcome back! What's been happening since your last check-in?",
      category: 'reflection',
      source: 'engagement'
    });
  }

  return prompts.slice(0, 4);
}

export async function expandEntry(shortEntry: string, userId: string): Promise<string> {
  try {
    const context = await getJournalContext(userId);

    const edgeFunctionUrl = `${env.VITE_SUPABASE_URL}/functions/v1/ai-coach-chat`;

    const response = await fetch(edgeFunctionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${env.VITE_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({
        messages: [
          {
            role: 'system',
            content: `You are a journaling assistant helping someone reflect more deeply. Given their brief journal entry, ask ONE thoughtful follow-up question that helps them explore their thoughts further. Keep it conversational and supportive. Don't lecture or give advice - just ask a good question. Maximum 25 words.`
          },
          {
            role: 'user',
            content: `My journal entry: "${shortEntry}"\n\nTime of day: ${context.timeOfDay}\n\nAsk me a follow-up question to help me reflect more deeply.`
          }
        ],
        model: modelConfig.getModelName(),
        temperature: 0.7,
        max_tokens: 100
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();
    return data.content || "What made you feel that way?";
  } catch (error) {
    logger.error('Error expanding entry:', error);
    return "What made you feel that way?";
  }
}

export function detectTags(text: string): string[] {
  const tagPatterns: Record<string, RegExp[]> = {
    stress: [/stress/i, /overwhelm/i, /anxious/i, /worried/i, /pressure/i, /tense/i],
    sleep: [/sleep/i, /tired/i, /exhausted/i, /rest/i, /insomnia/i, /nap/i, /woke/i],
    focus: [/focus/i, /concentrate/i, /distract/i, /productive/i, /attention/i],
    energy: [/energy/i, /energized/i, /motivated/i, /drained/i, /fatigue/i, /vigor/i],
    courage: [/courage/i, /brave/i, /fear/i, /afraid/i, /nervous/i, /confident/i, /bold/i],
    conversations: [/talk/i, /spoke/i, /conversation/i, /discuss/i, /meeting/i, /call/i, /chat/i],
    work: [/work/i, /job/i, /career/i, /boss/i, /coworker/i, /project/i, /deadline/i],
    health: [/workout/i, /exercise/i, /gym/i, /health/i, /diet/i, /eat/i, /weight/i],
    relationships: [/friend/i, /family/i, /partner/i, /relationship/i, /dating/i, /love/i],
    growth: [/learn/i, /improve/i, /grow/i, /progress/i, /better/i, /develop/i]
  };

  const detected: string[] = [];
  const lowerText = text.toLowerCase();

  for (const [tag, patterns] of Object.entries(tagPatterns)) {
    if (patterns.some(pattern => pattern.test(lowerText))) {
      detected.push(tag);
    }
  }

  return detected.slice(0, 4);
}

export function extractActionItems(text: string): string[] {
  const actionPatterns = [
    /i (?:will|need to|should|must|have to|want to|plan to|going to) ([^.!?\n]+)/gi,
    /(?:tomorrow|later|next week|soon)[,]? (?:i['']?ll|i will|i need to) ([^.!?\n]+)/gi,
    /my goal is to ([^.!?\n]+)/gi,
    /i['']?m going to ([^.!?\n]+)/gi
  ];

  const actions: string[] = [];

  for (const pattern of actionPatterns) {
    let match;
    while ((match = pattern.exec(text)) !== null) {
      const action = match[1].trim();
      if (action.length > 5 && action.length < 100) {
        const cleaned = action.charAt(0).toUpperCase() + action.slice(1);
        if (!actions.includes(cleaned)) {
          actions.push(cleaned);
        }
      }
    }
  }

  return actions.slice(0, 3);
}

export async function getRecentJournalSummary(userId: string, days: number = 7): Promise<string | null> {
  try {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const { data: entries, error } = await supabase
      .from('journal_entries')
      .select('quick_entry, win_of_day, challenge_faced, mood, created_at')
      .eq('user_id', userId)
      .gte('created_at', startDate.toISOString())
      .order('created_at', { ascending: false })
      .limit(10);

    if (error || !entries || entries.length === 0) {
      return null;
    }

    const summary = entries
      .map((e: { quick_entry?: string; win_of_day?: string; challenge_faced?: string }) => e.quick_entry || e.win_of_day || e.challenge_faced)
      .filter(Boolean)
      .slice(0, 5)
      .join(' | ');

    return summary || null;
  } catch (error) {
    logger.error('Error getting journal summary:', error);
    return null;
  }
}
